import type { SetRequired } from "type-fest";
import { v4 } from "uuid";
import { computed, reactive, ref, toValue } from "vue";
import * as math from "mathjs";
import { cloneDeep } from "lodash-es";

import { useSceneTree } from "../scene/sceneTree";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useEventEmitter } from "@core/composables/useEventEmitter";

import {
  evaluateMathNode,
  getExpressionMathNode
} from "./expressionFunctions";
import { operators, functions } from "./expressionParts";
import { getContrastColor } from "@/helpers/utility/Color";

import type { MaybeRefOrGetter } from "vue";
import type {
  InputConstantNode,
  InputConstantNodeOptions,
  InputFunctionNode,
  InputFunctionNodeOptions,
  InputItemNode,
  InputItemNodeOptions,
  InputNode,
  InputNodeGroup,
  InputNodeWithAnchors,
  InputOperatorNode,
  InputOperatorNodeOptions,
  InputParentNode,
  InputParenthesisNode,
  InputParenthesisNodeOptions,
  InputVariableNode,
  InputVariableNodeOptions,
  ExpressionOperator,
  NodeSelection
} from "./ExpressionInputTypes";
import type { ExpressionParseContext } from "../effect/Expression";
import type { FunctionName, ExpressionValue } from "./expressionFunctions";

export function useExpressionTree(
  context: MaybeRefOrGetter<ExpressionParseContext>,
  startExpression = ""
) {
  console.log("build new expression tree:", startExpression);

  const targetStore = useTargetStore();
  const resourceStore = useResourceStore();
  const indexStore = useIndexStore();

  const sceneTree = useSceneTree();

  const allItems = computed(() => {
    return [
      ...targetStore.orderedItems,
      ...resourceStore.orderedItems,
      ...indexStore.orderedItems
    ];
  });

  const itemsByUid = computed(() => {
    const items: {
      [uid: string]: {
        uid: string;
        title: string;
        type: string;
        id: number;
        fill: string | null;
        textColor: string | null;
      };
    } = {};
    for (const item of allItems.value) {
      const treeNode = sceneTree.elementNodes.get(item.uid);
      const sceneNode = treeNode?.node;
      items[item.uid] = {
        uid: item.uid,
        title: item.title,
        type: item.type,
        id: item.id,
        fill: sceneNode?.fill ?? null,
        textColor: sceneNode?.textColor ?? null
      };
    }
    return items;
  });

  const parseError = ref<string | null>(null);

  const state: { root: InputNode; insertMode: "coarse" | "fine"; parseError: string | null } = reactive({
    root: expressionToNodeTree(startExpression, toValue(context)),
    insertMode: "fine" as "coarse" | "fine",
    parseError: parseError
  });

  const eventEmitter = useEventEmitter<{
    "selection-change": [NodeSelection];
    "tree-updated": [InputNode];
  }>();

  function emitUpdate() {
    if (state.insertMode == "fine") {
      fixPrecedence(state.root);
    } else {
      disolveAllParentheses();
      wrapPrecendentsInParentheses();
    }

    history.save();

    eventEmitter.emit("tree-updated", state.root);
  }

  /**
   * Overwrite the current expression with a new one.
   */
  function setExpression(expression: string) {
    state.root = expressionToNodeTree(expression, toValue(context));

    // try to restore selection
    let restoredSelection: NodeSelection = {
      startNode: rootFocussable.node,
      startOffset: 0,
      endNode: rootFocussable.node,
      endOffset: 0
    };
    const oldStartNode = selection.startNode;
    const newStartNode = findComparableNode(state.root, oldStartNode);
    if (newStartNode) {
      restoredSelection.startNode = newStartNode.node;
      restoredSelection.startOffset = selection.startOffset;
    }
    const oldEndNode = selection.endNode;
    const newEndNode = findComparableNode(state.root, oldEndNode);
    if (newEndNode) {
      restoredSelection.endNode = newEndNode.node;
      restoredSelection.endOffset = selection.endOffset;
    }
    updateSelection(restoredSelection, true);
  }

  function findComparableNode(root: InputNode, oldNode: InputNode) {
    return findFirstNode(root, (node) => {
      if (node.type != oldNode.type) return false;
      if (node.type == "constant" && oldNode.type == "constant") {
        return node.value == oldNode.value;
      }
      if (node.type == "variable" && oldNode.type == "variable") {
        return node.code == oldNode.code;
      }
      if (node.type == "item" && oldNode.type == "item") {
        return node.uid == oldNode.uid;
      }
      if (node.type == "operator" && oldNode.type == "operator") {
        return node.op == oldNode.op && node.args.length == oldNode.args.length && node.args.every((arg, i) => {
          return arg.type == oldNode.args[i].type;
        });
      }
      return true;
    });
  }

  // ========================
  // SELECTION

  const rootFocussable = getFocussable(state.root as InputNode, getMaxOffset(state.root), "left");
  const selection: NodeSelection = reactive({
    startNode: rootFocussable.node,
    startOffset: rootFocussable.startOffset,
    endNode: rootFocussable.node,
    endOffset: rootFocussable.startOffset
  });

  function updateSelection(updates: Partial<NodeSelection>, overwriteHistory = false) {
    Object.assign(selection, {
      startNode: updates.startNode || selection.startNode,
      startOffset: updates.startOffset ?? selection.startOffset,
      endNode: updates.endNode || selection.endNode,
      endOffset: updates.endOffset ?? selection.endOffset
    });

    if (overwriteHistory) {
      history.updateSelection();
    }

    eventEmitter.emit("selection-change", selection);
  }

  function selectAll() {
    updateSelection({
      startNode: state.root,
      startOffset: 0,
      endNode: state.root,
      endOffset: getMaxOffset(state.root)
    });
  }

  function setInsertMode(mode: "coarse" | "fine") {
    state.insertMode = mode;

    // Update selection to match new mode
    if (mode == "coarse") {
      if (selection.startNode == selection.endNode) {
        updateSelection({
          startOffset: 0,
          endOffset: getMaxOffset(selection.startNode)
        });
      }
    } else {
      updateSelection({
        startNode: selection.endNode,
        startOffset: selection.endOffset
      });
    }
  }

  // ========================
  // HISTORY

  type ExpressionHistoryEntry = {
    root: InputNode;
    selection: NodeSelection;
  };

  const history = function() {
    // last state is the current state
    const history: ExpressionHistoryEntry[] = [];
    const future: ExpressionHistoryEntry[] = [];

    function save() {
      history
        .unshift({
          root: cloneDeep(state.root),
          selection: cloneDeep(selection)
        });
        
      future.length = 0;
    }

    function undo() {
      if (history.length < 2) {
        return;
      }

      future.unshift(history.shift() as ExpressionHistoryEntry);

      const previous = cloneDeep(history[0]);
      restore(previous);

      eventEmitter.emit("selection-change", selection);
      eventEmitter.emit("tree-updated", state.root);
    }

    function redo() {
      if (future.length == 0) {
        return;
      }

      history.unshift(future.shift() as ExpressionHistoryEntry);
      
      const restored = cloneDeep(history[0]);
      restore(restored);

      eventEmitter.emit("selection-change", selection);
      eventEmitter.emit("tree-updated", state.root);
    }

    function restore(entry: ExpressionHistoryEntry) {
      state.root = entry.root;
      selection.startNode = findNode(entry.selection.startNode.id)?.node || selection.startNode;
      selection.startOffset = entry.selection.startOffset;
      selection.endNode = findNode(entry.selection.endNode.id)?.node || selection.endNode;
      selection.endOffset = entry.selection.endOffset;
    }

    function updateSelection() {
      if (history.length == 0) {
        return;
      }

      console.log("update selection in history");
      history[0].selection = cloneDeep(selection);
    }

    return {
      save,
      undo,
      redo,
      updateSelection
    };
  }();
  history.save();

  // ========================
  // NODE CREATION
  //#region NodeCreation

  function createConstant(
    options: InputConstantNodeOptions
  ): InputConstantNode {
    return reactive({
      type: "constant",
      id: options.id ?? v4(),
      value: options.value
    });
  }
  function createOperator(
    options: InputOperatorNodeOptions
  ): InputOperatorNode {
    const op = getValidOperator(options.op);

    return reactive({
      type: "operator",
      id: options.id ?? v4(),
      op: op,
      args: [...options.args]
    });
  }
  function createFunction(
    options: InputFunctionNodeOptions
  ): InputFunctionNode {
    return reactive({
      type: "function",
      id: options.id ?? v4(),
      name: options.name,
      args: [...options.args]
    });
  }
  function createItem(options: InputItemNodeOptions): InputItemNode {
    const item = computed(() => itemsByUid.value[options.uid]);

    const fill = computed(
      () => item.value?.fill || "#fAfAfA"
    );
    const textColor = computed(() => {
      if (item.value?.textColor) return item.value.textColor;
      return getContrastColor(fill.value);
    });
    const title = computed(() => item.value?.title ?? "???");

    return reactive({
      type: "item",
      id: options.id ?? v4(),
      uid: options.uid,
      title,
      fill,
      textColor,
      item: item,
      valid: computed(() => item.value != null)
    });
  }
  function createVariable(
    options: InputVariableNodeOptions
  ): InputVariableNode {
    return reactive({
      type: "variable",
      id: options.id ?? v4(),
      code: options.code
    });
  }
  function createParenthesis(
    options: InputParenthesisNodeOptions
  ): InputParenthesisNode {
    return reactive({
      type: "parenthesis",
      id: options.id ?? v4(),
      args: [...options.args],
      opened: options.opened,
      closed: options.closed
    });
  }

  //#endregion

  // ========================
  // CONVERSION
  //#region Conversion

  function expressionToNodeTree(
    expression: string,
    context: ExpressionParseContext
  ) {
    parseError.value = null;
    try {
      const mathTree = getExpressionMathNode(expression.trim(), context);
  
      return mathNodeToInputNode(mathTree, context);
    } catch (error) {
      console.error("Error parsing expression:", error, expression);
      parseError.value = error instanceof Error ? error.message : String(error);
      let parsed: number | string = parseFloat(expression);
      if (isNaN(parsed) || !isFinite(parsed) || parsed == null) {
        parsed = "";
      }
      return createConstant({ value: "" + parsed });
    }
  }

  function mathNodeToInputNode(
    node: math.MathNode,
    context: ExpressionParseContext
  ): InputNode {
    if (node instanceof math.ConstantNode) {
      return createConstant({
        value: `${node.value ?? ""}`
      });
    }

    if (node instanceof math.SymbolNode) {
      const name = node.name;
      const [type] = name.split("_") ?? ["", "-1"];

      // Check if the symbol is a reference to
      // a net item (target, resource, index):
      if (type == "target" || type == "index" || type == "resource") {
        return createItem({
          uid: name
        });
      }

      // anything that does not fit into any of
      // the above categories is a variable.
      return createVariable({
        code: name
      });
    }

    if (node instanceof math.OperatorNode) {
      const args = node.args.map((arg: math.MathNode) =>
        mathNodeToInputNode(arg, context)
      );

      if (args.length < 2) {
        args.unshift(createConstant({ value: "" }));
      }

      return createOperator({
        op: node.op,
        args
      });
    }

    if (node instanceof math.ParenthesisNode) {
      return createParenthesis({
        args: [mathNodeToInputNode(node.content, context)],
        opened: true,
        closed: true
      });
    }

    if (node instanceof math.FunctionNode) {
      return createFunction({
        name: node.fn.name,
        args: node.args.map((arg: math.MathNode) =>
          mathNodeToInputNode(arg, context)
        )
      });
    }

    throw new Error("non-implemented math node " + node.type);
  }

  function inputNodeToMathNode(
    node: InputNode,
    context: ExpressionParseContext
  ): math.MathNode {
    if (node.type == "constant") {
      return new math.ConstantNode(node.value == "" ? 0 : parseFloat(node.value || '0'));
    }

    if (node.type == "operator") {
      const op = node.op || "*";
      return new math.OperatorNode(op, operators[op].name, [
        inputNodeToMathNode(node.args[0], context),
        inputNodeToMathNode(node.args[1], context)
      ]);
    }

    if (node.type == "function") {
      return new math.FunctionNode(
        node.name,
        node.args.map((arg) => inputNodeToMathNode(arg, context))
      );
    }
    if (node.type == "item") {
      return new math.SymbolNode(node.uid);
    }
    if (node.type == "variable") {
      return new math.SymbolNode(node.code);
    }
    if (node.type == "parenthesis") {
      return new math.ParenthesisNode(
        inputNodeToMathNode(node.args[0], context)
      );
    }
    throw new Error("non-implemented node type " + node);
  }

  function toExpressionString() {
    return nodeToExpression(state.root);
  }

  function nodeToExpression(node: InputNode): string {
    if (node.type == "constant") {
      return node.value;
    }
    if (node.type == "operator") {
      let op: string = node.op;
      if (op == "==" && node.id == state.root.id) {
        if (node.args[0].type == "constant" && node.args[0].value == "") {
          op = "=";
        }
      }
      return node.args.map(nodeToExpression).join(op);
    }
    if (node.type == "function") {
      return `${node.name}(${node.args.map(nodeToExpression).join(",")})`;
    }
    if (node.type == "item") {
      return node.uid;
    }
    if (node.type == "variable") {
      return node.code;
    }
    if (node.type == "parenthesis") {
      return `(${node.args
        .map(nodeToExpression)
        .join(",")})`;
    }
    return "";
  }

  //#endregion

  function replaceNode(
    targetNode: InputNode,
    newNode: InputNode,
    rootNode?: InputNode
  ): NodeFocusPosition {
    const targetParent = findNodeIn(rootNode || state.root, targetNode.id)
      ?.parent;

    if (targetParent == null) {
      if (rootNode) {
        return getFocussable(newNode, 0);
      }

      // If the node to replace has no parent, it's the root node.
      // In that case, replace the root.
      state.root = reactive(newNode);
      return getFocussable(newNode, 0);
    }

    // overwrite the old node with the new one
    const index = targetParent.args.indexOf(targetNode);
    targetParent.args.splice(index, 1, newNode);

    // resolve focus node first
    let { node: focusNode, startOffset: offset } = getFocussable(newNode, 0 as number);

    // remove implicit multiplications
    const newParent: InputNode | undefined | null = targetParent;
    const removed = removeImplicitMultiplications(newParent, focusNode, offset);
    if (removed) {
      if (removed.node) focusNode = removed.node;
      if (removed.startOffset != null) offset = removed.startOffset;
    }

    return getFocussable(focusNode, offset);
  }

  function updateNode<T extends InputNode>(
    node: InputNode,
    updates: Partial<T>
  ) {
    Object.assign(node, updates);

    emitUpdate();
  }

  function replaceFunction(
    functionNode: InputFunctionNode,
    name: FunctionName
  ) {
    const newFunction = createFunction({
      name,
      args: fillFunctionArguments(name, functionNode.args)
    });

    replaceNode(functionNode, newFunction);

    return newFunction;
  }

  function getValidOperator(op: ExpressionOperator | "!" | "=" | "") {
    if (op == "!") {
      return "!=";
    }
    if (op == "=") {
      return "==";
    }
    return op;
  }

  function removeImplicitMultiplications(
    mainNode: InputNode | undefined,
    focusNode?: InputNode,
    focusOffset?: number
  ) {
    if (mainNode == null) {
      return null;
    }

    let offset = focusOffset;

    while (mainNode?.type == "operator" && mainNode.op == "") {
      const firstArg: InputNode = mainNode.args[0];
      const secondArg: InputNode = mainNode.args[1];

      if (firstArg.type == "constant" && firstArg.value == "") {
        // remove first arg
        replaceNode(mainNode, secondArg);
        if (focusNode == firstArg) {
          focusNode = secondArg;
          offset = 0;
        }
        mainNode = findNode(mainNode.id)?.parent;
        continue;
      }

      if (secondArg.type == "constant" && secondArg.value == "") {
        // remove second arg
        replaceNode(mainNode, firstArg);
        if (focusNode == secondArg) {
          focusNode = firstArg;
          offset = getMaxOffset(firstArg);
        }
        mainNode = findNode(mainNode.id)?.parent;
        continue;
      }

      if (firstArg.type == "constant" && secondArg.type == "constant") {
        // merge children:
        offset = firstArg.value.length;
        firstArg.value += secondArg.value;

        // in the case that we replace the newly created node,
        // we need to update the reference to the new node.
        if (secondArg == focusNode) {
          focusNode = firstArg;
        }

        replaceNode(mainNode, firstArg);
        mainNode = findNode(firstArg.id)?.parent;
        continue;
      }

      if (firstArg.type == "operator") {
        if (secondArg.type == "constant") {
          const rightmostNode = findRight(
            firstArg,
            (n) => n.type != "operator"
          );
          if (rightmostNode == null || rightmostNode.node.type != "constant") {
            break;
          }

          // merge children:
          offset = rightmostNode.node.value.length;
          rightmostNode.node.value += secondArg.value;

          // in the case that we replace the newly created node,
          // we need to update the reference to the new node.
          if (secondArg == focusNode) {
            focusNode = rightmostNode.node;
          }

          replaceNode(mainNode, firstArg);
          mainNode = findNode(firstArg.id)?.parent;
          continue;
        }

        const rightmostChild = findRight(firstArg, (n) => n.type != "operator");
        if (
          rightmostChild &&
          rightmostChild.node.type == "constant" &&
          rightmostChild.node.value == ""
        ) {
          // remove rightmost child
          replaceNode(rightmostChild.node, secondArg);
          replaceNode(mainNode, firstArg);
          mainNode = findNode(firstArg.id)?.parent;
          continue;
        }
      }

      if (firstArg.type == "constant" && secondArg.type == "operator") {
        const leftmostNode = findLeft(secondArg, (n) => n.type != "operator");
        if (leftmostNode == null || leftmostNode.node.type != "constant") {
          break;
        }

        // merge children:
        offset = firstArg.value.length;
        leftmostNode.node.value = firstArg.value + leftmostNode.node.value;

        // in the case that we replace the newly created node,
        // we need to update the reference to the new node.
        if (firstArg == focusNode || secondArg == focusNode) {
          focusNode = leftmostNode.node;
        }

        replaceNode(mainNode, secondArg);
        mainNode = findNode(secondArg.id)?.parent;
        continue;
      }

      break;
    }

    return getFocussable(focusNode, offset, "left");
  }

  function getMaxOffset(node: InputNode) {
    if (node.type == "constant") {
      return node.value.length;
    }
    return 1;
  }

  /**
   * Ensure we return a node that can be focused.
   */
  function getFocussable<
    N extends InputNode | undefined,
    O extends number | undefined
  >(
    node: N,
    offset: O,
    direction: "left" | "right" = "right"
  ): NodeFocusPosition<N, O> {
    if (node == null) return { node, startOffset: offset };

    if (node.type == "operator") {
      if (offset == 0) {
        const innerOffset =
          direction == "right" ? 0 : getMaxOffset(node.args[0]);
        return getFocussable(node.args[0], innerOffset, direction) as NodeFocusPosition<
          N,
          O
        >;
      } else {
        const innerOffset =
          direction == "right" ? 0 : getMaxOffset(node.args[1]);
        return getFocussable(node.args[1], innerOffset, direction) as NodeFocusPosition<
          N,
          O
        >;
      }
    }
    return { node, startOffset: offset };
  }

  /**
   * Get a reference to a node from a node
   * @param node The root node to search in.
   * @param nodeId The id of the node to find.
   * @param parent The parent node of the node we're searching in.
   * @returns The node, the parent node, and the index of the node in the parent's args array.
   */
  function findNodeIn<N extends InputNode>(
    node: InputNode,
    nodeId: string,
    parent?: InputParentNode
  ): InputNodeGroup<N> | null {
    return findFirstNode<N>(node, (node) => node.id == nodeId, parent);
  }

  function findNode(nodeId: string) {
    return findNodeIn(state.root, nodeId);
  }

  /**
   * Get the last common parent of two nodes.
   * @param nodeA
   * @param nodeB
   * @returns The last common parent of the two nodes, or null if there is none.
   */
  function getCommonParent(
    nodeA: InputNode,
    nodeB: InputNode
  ): {
    parent: InputParentNode;
    indexA: number;
    indexB: number;
    nodePathA: InputParentNode[]; // remaining node path after the common parent
    nodePathB: InputParentNode[]; // remaining node path after the common parent
  } | null {
    const nodePathA = getNodePath(state.root, nodeA);
    const nodePathB = getNodePath(state.root, nodeB);

    if (nodePathA == null || nodePathB == null) {
      return null;
    }

    let i = 0;
    let commonParent: InputParentNode | null = null;
    while (nodePathA[i] == nodePathB[i] && nodePathA[i] != null) {
      commonParent = nodePathA[i];
      i++;
    }

    if (commonParent == null) {
      return null;
    }

    const indexA = commonParent.args.indexOf(nodePathA[i] || nodeA);
    const indexB = commonParent.args.indexOf(nodePathB[i] || nodeB);

    return {
      parent: commonParent,
      indexA,
      indexB,
      nodePathA: nodePathA.slice(i),
      nodePathB: nodePathB.slice(i)
    };
  }

  /**
   * Return a list of all parents of a node, up to the root node.
   * @param node
   */
  function getNodePath(
    srcNode: InputNode,
    nodeToFind: InputNode
  ): InputParentNode[] | null {
    // Do NOT use findNode here,
    // because that function is expensive.
    if (srcNode == nodeToFind) {
      return [];
    }
    if (srcNode.type == "constant") {
      return null;
    }
    if ("args" in srcNode) {
      for (const arg of srcNode.args) {
        const path = getNodePath(arg, nodeToFind);
        if (path != null) {
          path.unshift(srcNode);
          return path;
        }
      }
    }
    return null;
  }

  /**
   * Return a node path that includes the index of each node in its parent's args array.
   */
  function getIndexedNodePath(
    srcNode: InputNode,
    nodeToFind: InputNode,
    index: number = -1,
    parentPath: string = ""
  ): { node: InputNode; index: number, path: string }[] | null {
    if (srcNode == nodeToFind) {
      return [{ node: srcNode, index: index, path: parentPath }];
    }
    if (srcNode.type == "constant") {
      return null;
    }
    if (srcNode.type == "parenthesis") {
      return getIndexedNodePath(srcNode.args[0], nodeToFind, 0, parentPath);
    }
    if ("args" in srcNode) {
      for (let i = 0; i < srcNode.args.length; i++) {
        const arg = srcNode.args[i];
        const path = getIndexedNodePath(arg, nodeToFind, i, parentPath + "." + i);
        if (path != null) {
          path.unshift({ node: srcNode, index: index, path: parentPath });
          return path;
        }
      }
    }
    return null;
  }

  function mergeWithPreviousNode(
    node: InputNode,
    offset: number
  ): NodeFocusPosition | undefined {
    const found = findNode(node.id);
    if (found == null) {
      console.warn("node not found", node.id);
      return;
    }
    const { parent, index } = found;

    let nodeToFocus: InputNode | null = node;

    if (node.type == "constant" && offset > 0) {
      // remove a character from the node
      node.value = node.value.slice(0, offset - 1) + node.value.slice(offset);

      return {
        node,
        startOffset: offset - 1
      };
    }

    if (node.type == "operator") {
      console.warn("Operator node should not be passed.");
      return;
    }

    if (isNodeWithAnchors(node)) {
      if (offset >= 1) {
        if (node.type == "parenthesis") {
          if (node.closed) {
            const contentNode = node.args[0];
            const nodeToFocus =
              findRight(contentNode, (n) => n.type != "operator")?.node ||
              contentNode;

            openParenthesis(node);

            return {
              node: nodeToFocus,
              startOffset: getMaxOffset(nodeToFocus)
            };
          }
        }

        // delete this node.
        const constantNode = createConstant({ value: "" });
        return replaceNode(node, constantNode);
      } else {
        // offset == 0

        if (parent == null || index == null) {
          return replaceNode(node, createConstant({ value: "" }));
        }

        if (parent?.type == "parenthesis") {
          return replaceNode(parent, node);
        }

        if (parent.type == "function") {
          // In a function, we do not remove anything coming before the arg.
          if (index > 0) {
            const previousArg = parent.args[index - 1];
            return {
              node:
                getRightmostUnbreakableNode(previousArg)?.node || previousArg,
              startOffset:
                previousArg.type == "constant" ? previousArg.value.length : 1
            };
          } else if (parent.args.length == 1) {
            // remove the function node if it has only one argument
            return replaceNode(parent, node);
          } else {
            return {
              node: parent,
              startOffset: 0
            };
          }
        }

        if (parent.type == "operator" && index == 1) {
          if (parent.op != "") {
            parent.op = "";
            const removed = removeImplicitMultiplications(parent, node);

            return {
              node: removed?.node ?? node,
              startOffset: removed?.startOffset ?? 0
            };
          }

          const previousNode = parent.args[0];

          const previousNodeGroup = findRight(
            previousNode,
            (node) => node.type != "operator",
            [parent]
          );

          if (previousNodeGroup?.node.type == "constant") {
            const previousConstant = previousNodeGroup.node;

            if (previousConstant.value.length > 0) {
              previousConstant.value = previousConstant.value.slice(0, -1);

              removeImplicitMultiplications(previousNodeGroup.parent, node);
              return { node: node, startOffset: 0 };
            }

            // Nothing left in node, remove it.
            removeNode(previousConstant);

            return { node: node, startOffset: 0 };
          }
        }
      }
      // console.warn("nothing to remove", node.type, offset);
      // return;
    }

    if (parent == null || index == null) {
      console.warn("no parent", node);
      return;
    }

    const nodePath = getNodePath(state.root, node);
    if (nodePath == null) {
      console.warn("node is not in the tree", node);
      return;
    }

    // remove any parent parenthesis
    const reversedPath = nodePath.slice().reverse();
    let ancestorChild: InputNode = node;
    for (const ancestor of reversedPath) {
      if (ancestor.type == "function") {
        if (ancestor.args.length == 1) {
          return replaceNode(ancestor, ancestorChild);
        }
        console.warn("Can't remove function node");
        return;
      }
      if (ancestor.type == "parenthesis") {
        console.log("← remove parent parenthesis");
        // replace the parenthesis with its content
        return replaceNode(ancestor, ancestorChild);
      }

      if (ancestor.type == "operator") {
        const index = ancestor.args.indexOf(ancestorChild);
        if (index == 1) {
          break;
        }
      }
      ancestorChild = ancestor;
    }

    const _previousNode = findPreviousNode(node);
    if (_previousNode == null) {
      console.warn("No previous node");
      return;
    }
    const previousNode = _previousNode.node;

    if (parent.type != "operator") {
      console.warn("common parent is not an operator", node, previousNode);
      return;
    }

    // Check if it is theoretically possible to join the two nodes

    // 1st check: Do they share a common ancestor?
    const previousNodePath = getNodePath(state.root, previousNode);

    if (nodePath == null || previousNodePath == null) {
      console.warn("Can't join nodes that are not in the ");
      return;
    }

    // get common ancestor
    let i = 0;
    let commonParent: InputParentNode | null = null;
    while (nodePath[i] != null && nodePath[i] == previousNodePath[i]) {
      commonParent = nodePath[i];
      i++;
    }
    if (commonParent == null) {
      console.warn("No common parent");
      return;
    }

    // If there is a function in the path, we can't merge the nodes.
    // e.g. "6 * round(6)" <--- "5"
    // can't be merged, since the "5" would have to be moved into the function "round".
    const nodePathRest = nodePath.slice(i);
    const previousNodePathRest = previousNodePath.slice(i);
    if (
      nodePathRest.some((node) => node.type == "function") ||
      previousNodePathRest.some((node) => node.type == "function")
    ) {
      console.warn("Can't merge function nodes");
      return;
    }

    // checks are done.
    // NOW, let's get to the joining part.

    let newStartOffset = 0;

    // Separate the parts into two groups:
    const previousNodeGroup = commonParent.args[0];
    const thisNodeGroup = commonParent.args[1];

    const prevGroup = getRightmostUnbreakableNode(previousNode);
    const previousInseparableNode = prevGroup?.node;
    const previousInseparableNodePath = prevGroup?.path || [];

    if (prevGroup == null) {
      console.warn("no previous group");
      return { node: node, startOffset: offset };
    }

    const parentParenthesis = previousInseparableNodePath.find(
      (n): n is InputParenthesisNode => isParenthesisNode(n) && n.closed
    );

    if (commonParent.type == "operator" && commonParent.op != "") {
      console.log("← make operator implicit", commonParent.op);

      commonParent.op = "";
      newStartOffset = 0;

      const removed = removeImplicitMultiplications(
        commonParent,
        node,
        newStartOffset
      );
      if (removed?.node) {
        nodeToFocus = removed.node;
        newStartOffset = removed.startOffset || 0;
        console.log("new focus", nodeToFocus, newStartOffset);
      } else {
        console.log("removed", removed);
      }

      return { node: nodeToFocus, startOffset: newStartOffset };
    }

    if (previousInseparableNode?.type == "constant") {
      if (node.type == "constant") {
        nodeToFocus = previousInseparableNode;
        newStartOffset = previousInseparableNode.value.length;
        previousInseparableNode.value += node.value;

        if (thisNodeGroup == node) {
          replaceNode(commonParent, previousNodeGroup);
        } else {
          replaceNode(node, previousNodeGroup);
          replaceNode(commonParent, thisNodeGroup);
        }
      } else {
        // remove a character from the previous node
        previousInseparableNode.value = previousInseparableNode.value.slice(
          0,
          -1
        );
        newStartOffset = previousInseparableNode.value.length;
      }
    }

    // a inseparable node before this one and we pressed backspace.
    // remove the node and the operator
    else if (
      previousInseparableNode?.type == "function" ||
      previousInseparableNode?.type == "item" ||
      previousInseparableNode?.type == "variable"
    ) {
      console.log("previous node is inseparable", previousInseparableNode.type);

      console.log("common parent", commonParent.type, commonParent.id);

      if (prevGroup.parent?.type == "operator") {
        replaceNode(commonParent, commonParent.args[0]);

        const replaced = replaceNode(
          previousInseparableNode,
          commonParent.args[1]
        );

        if (replaced?.node) {
          nodeToFocus = replaced.node;
          newStartOffset = replaced.startOffset || 0;
        }
      } else {
        removeNode(previousInseparableNode);
      }
    }

    if (parentParenthesis) {
      console.log("← open previous parenthesis");
      openParenthesis(parentParenthesis);
    }

    return { node: nodeToFocus, startOffset: newStartOffset };
  }

  function openParenthesis(parenthesis: InputParenthesisNode) {
    parenthesis.closed = false;

    if (parenthesis.opened == false) {
      replaceNode(parenthesis, parenthesis.args[0]);
      return;
    }

    // extend parent parenthesis as far as possible
    let parentGroup = findNode(parenthesis.id);
    while (
      parentGroup?.parent &&
      parentGroup.parent.type == "operator" &&
      parentGroup.index == 0
    ) {
      const operator = parentGroup.parent;
      operator.args[0] = parenthesis.args[0];
      parenthesis.args[0] = operator;
      replaceNode(operator, parenthesis);
      removeImplicitMultiplications(operator);
      parentGroup = findNode(parenthesis.id);
    }
  }

  function removeNode(node: InputNode) {
    const group = findNode(node.id);
    if (group == null) {
      console.warn("could not find node to remove");
      return null;
    }

    const { parent, index } = group;
    if (parent == null || index == null) {
      // Node has no parent, so it must be the root node.
      console.log("replace root node");
      return replaceNode(state.root, createConstant({ value: "" }));
    }

    if (parent.type == "parenthesis") {
      // remove the parenthesis
      console.log("remove parenthesis");
      return replaceNode(parent, createConstant({ value: "" }));
    }

    if (parent.type == "operator") {
      // remove the operator and replace it with the other argument
      const otherArg = parent.args[1 - index];
      return replaceNode(parent, otherArg);
    }

    if (parent.type == "function") {
      return replaceNode(node, createConstant({ value: "" }));
    }

    // console.warn("can't remove node in parent", parent.type);

    return null;
  }

  function findPreviousNode(_node: InputNode) {
    const parent = findNode(_node.id)?.parent;
    if (parent == null) {
      return null;
    }

    if ("args" in parent) {
      const index = parent.args.indexOf(_node);
      if (index > 0) {
        return { node: parent.args[index - 1], parent };
      }

      let grandParent: InputNode | undefined = parent;
      do {
        // Move up one level
        // and return the last node
        const parentGroup = findNode(grandParent.id);
        const parentIndex = parentGroup?.index;
        grandParent = parentGroup?.parent;

        if (grandParent == null) {
          return null;
        }
        if (parentIndex == null) {
          console.warn("No parent index");
          return null;
        }
        if ("args" in grandParent) {
          if (parentIndex > 0) {
            return {
              node: grandParent.args[parentIndex - 1],
              parent: grandParent
            };
          }
        }
      } while (grandParent != null);
    }

    return null;
  }

  function findPreviousInnerNode(node: InputNode) {
    const previousNode = findPreviousNode(node)?.node;

    if (!previousNode) return null;

    return getRightmostUnbreakableNode(previousNode);
  }

  /**
   * Is this a node the cursor can be placed in?
   */
  function isTargetableNode(node: InputNode) {
    return node.type != "operator";
  }

  function findPreviousCursorSpot(
    node: InputNode,
    offset: number
  ): NodeFocusPosition | null {
    let searchBaseNode = null;

    if (offset > 0) {
      if ("args" in node) {
        const lastArg = node.args[node.args.length - 1];
        searchBaseNode = lastArg;
      } else {
        return getFocussable(node, offset - 1, "left");
      }
    } else {
      const nodeGroup = findNode(node.id);
      const parent = nodeGroup?.parent;

      console.log("parent", parent);

      const commonWithPrevious = findCommonWithPrevious(node);
      console.log("common with previous", commonWithPrevious.node);
      if (!("args" in commonWithPrevious.node)) {
        // If the node has no parents, it is the first node.
        // Return the node itself.
        return getFocussable(node, 0, "left");
      }

      if (commonWithPrevious.node.type == "operator") {
        if (commonWithPrevious.childIndex == 0) {
          return getFocussable(commonWithPrevious.node.args[0], 0);
        } else {
          searchBaseNode = commonWithPrevious.node.args[0];
        }
      } else if (
        commonWithPrevious.node.type == "function" ||
        commonWithPrevious.node.type == "parenthesis"
      ) {
        // Already at the first argument of the parent?
        if (commonWithPrevious.childIndex == 0) {
          // Move to the "before" cursor anchor.
          return getFocussable(commonWithPrevious.node, 0, "left");
        } else {
          // Move to previous argument of the parent
          // Search for the rightmost targetable node inside
          searchBaseNode =
            commonWithPrevious.node.args[commonWithPrevious.childIndex - 1];
        }
      } else {
        throw new Error("undefined case: parent is " + commonWithPrevious.node);
      }
    }

    const found = findRight(searchBaseNode, isTargetableNode);
    if (!found) return null;

    return getFocussable(found.node, getMaxOffset(found.node), "left");
  }

  function findNextCursorSpot(
    node: InputNode,
    offset: number
  ): NodeFocusPosition | null {
    let searchBaseNode = null;

    if (offset < getMaxOffset(node)) {
      if ("args" in node) {
        const firstArg = node.args[0];
        searchBaseNode = firstArg;
      } else {
        return getFocussable(node, offset + 1);
      }
    } else {
      const commonWithNext = findCommonWithNext(node);

      if (commonWithNext.node == node) {
        return getFocussable(node, offset);
      }

      if (!("args" in commonWithNext.node)) {
        // If the node has no parents, it is the first node.
        // Return the node itself.
        return getFocussable(node, offset);
      }

      if (commonWithNext.node.type == "operator") {
        // At the end of the operator?
        // This means there is no next node - stay here.
        if (commonWithNext.childIndex >= 1) {
          return getFocussable(node, offset);
        } else {
          searchBaseNode = commonWithNext.node.args[1];
        }
      } else if (
        commonWithNext.node.type == "function" ||
        commonWithNext.node.type == "parenthesis"
      ) {
        const maxChildIndex = commonWithNext.node.args.length - 1;
        if (commonWithNext.childIndex == maxChildIndex) {
          // We're at the end of the parent, so we can't switch to the next argument.
          // Instead, move to the "after" cursor anchor.
          return getFocussable(commonWithNext.node, 1);
        } else {
          // Move to next argument of the parent
          // Search for the leftmost targetable node inside
          searchBaseNode =
            commonWithNext.node.args[commonWithNext.childIndex + 1];
        }
      } else {
        throw new Error("undefined case: parent is " + commonWithNext.node);
      }
    }

    if (searchBaseNode == null) {
      console.warn("search base node is null", offset, node);
      return null;
    }

    const found = findLeft(searchBaseNode, isTargetableNode);
    if (!found) return null;

    return getFocussable(found.node, 0);
  }

  function findPreviousConstantNode(node: InputNode) {
    const previousNode = findPreviousNode(node)?.node;

    let n: InputNode | undefined = previousNode;
    while (n) {
      const constantNode = findLastConstantNode(n);
      if (constantNode) {
        return constantNode;
      }
      n = findPreviousNode(n)?.node;
    }

    return null;
  }

  function findNextConstantNode(node: InputNode) {
    const nextNode = findNextNode(node)?.node;

    let n: InputNode | undefined = nextNode;
    while (n) {
      const constantNode = findFirstConstantNode(n);
      if (constantNode) {
        return constantNode;
      }
      n = findNextNode(n)?.node;
    }

    return null;
  }

  function findLastNode<N extends InputNode>(
    node: InputNode,
    findFunction: (node: InputNode) => boolean,
    parent?: InputParentNode
  ): InputNodeGroup<N> | null {
    if (findFunction(node)) {
      const index = parent?.args.indexOf(node);
      return {
        node: node as N,
        parent,
        index
      };
    }
    if ("args" in node) {
      for (let i = node.args.length - 1; i >= 0; i--) {
        const arg = node.args[i];
        const found = findLastNode<N>(arg, findFunction, node);
        if (found != null) {
          return found;
        }
      }
    }
    return null;
  }

  function findFirstNode<N extends InputNode>(
    node: InputNode,
    findFunction: (node: InputNode) => boolean,
    parent?: InputParentNode
  ): InputNodeGroup<N> | null {
    if (findFunction(node)) {
      const index = parent?.args.indexOf(node);
      return {
        node: node as N,
        parent,
        index
      };
    }
    if ("args" in node) {
      for (const arg of node.args) {
        const found = findFirstNode<N>(arg, findFunction, node);
        if (found != null) {
          return found;
        }
      }
    }
    return null;
  }

  function findNextNode(
    node: InputNode
  ): { node: InputNode; parent: InputParentNode } | null {
    const found = findNode(node.id);
    if (!found) return null;

    const { parent, index } = found;
    if (parent == null) {
      return null;
    }

    if ("args" in parent) {
      if (index == null) {
        console.warn("No index");
        return null;
      }

      if (index < parent.args.length - 1) {
        return { node: parent.args[index + 1], parent };
      }

      let grandParent: InputNode | undefined = parent;
      do {
        // Move up one level
        // and return the last node
        const parentGroup = findNode(grandParent.id);
        const parentIndex = parentGroup?.index;
        grandParent = parentGroup?.parent;

        if (grandParent == null) {
          return null;
        }
        if (parentIndex == null) {
          console.warn("No parent index");
          return null;
        }
        if ("args" in grandParent) {
          if (parentIndex < grandParent.args.length - 1) {
            return {
              node: grandParent.args[parentIndex + 1],
              parent: grandParent
            };
          }
        }
      } while (grandParent != null);
    }

    return null;
  }

  /**
   * Traverse a node tree, searching only the rightmost nodes.
   */
  function findRight(
    node: InputNode,
    searchFunction: (node: InputNode) => boolean,
    path: InputParentNode[] = []
  ) {
    if (searchFunction(node)) {
      return {
        node,
        parent: path[path.length - 1],
        path,
        index: path[path.length - 1]?.args.indexOf(node)
      };
    }
    if ("args" in node) {
      const rightmostArg = node.args[node.args.length - 1];
      return findRight(rightmostArg, searchFunction, [...path, node]);
    }
    return null;
  }

  /**
   * Traverse a node tree, searching only the leftmost nodes.
   */
  function findLeft(
    node: InputNode,
    searchFunction: (node: InputNode) => boolean,
    path: InputParentNode[] = []
  ) {
    if (searchFunction(node)) {
      return {
        node,
        parent: path[path.length - 1],
        path,
        index: path[path.length - 1]?.args.indexOf(node)
      };
    }
    if ("args" in node) {
      const leftmostArg = node.args[0];
      return findLeft(leftmostArg, searchFunction, [...path, node]);
    }
    return null;
  }

  /**
   * Get the nearest parent that has previous children or can't be broken up.
   */
  function findCommonWithPrevious(
    node: InputNode
  ): InputNodeGroup<InputNode> & { childIndex: number } {
    const nodePath = getNodePath(state.root, node);
    if (nodePath == null) throw new Error("could not find Node");

    let i = nodePath.length - 1;
    let n = node;
    let index = 0;
    while (i >= 0) {
      const parent = nodePath[i];
      index = parent.args.indexOf(n);

      if (
        parent.type == "function" ||
        parent.type == "parenthesis" ||
        index > 0
      ) {
        const grandParent = nodePath[i - 1];
        return {
          node: parent,
          childIndex: index,
          parent: grandParent || undefined,
          index: grandParent?.args.indexOf(parent) || undefined
        };
      }

      i--;
      n = parent;
    }

    return {
      node: state.root,
      parent: undefined,
      index: undefined,
      childIndex: index
    };
  }

  /**
   * Get the nearest parent that has next children or can't be broken up.
   */
  function findCommonWithNext(
    node: InputNode
  ): InputNodeGroup<InputNode> & { childIndex: number } {
    const nodePath = getNodePath(state.root, node);
    if (nodePath == null) throw new Error("could not find Node");

    let i = nodePath.length - 1;
    let n = node;
    let index = 0;
    while (i >= 0) {
      const parent = nodePath[i];
      index = parent.args.indexOf(n);

      if (
        parent.type == "function" ||
        parent.type == "parenthesis" ||
        index < parent.args.length - 1
      ) {
        const grandParent = nodePath[i - 1];
        return {
          node: parent,
          childIndex: index,
          parent: grandParent || undefined,
          index: grandParent?.args.indexOf(parent) || undefined
        };
      }

      i--;
      n = parent;
    }

    return {
      node: state.root,
      parent: undefined,
      index: undefined,
      childIndex: index
    };
  }

  /**
   * Moves recursively through the children of the given node
   * and returns the first rightmost node that is not an operator.
   */
  function getRightmostUnbreakableNode(
    node: InputNode,
    path: InputParentNode[] = []
  ) {
    return findRight(
      node,
      (node) => node.type != "operator" && node.type != "parenthesis",
      path
    );
  }

  function findLastConstantNode(
    node: InputNode,
    parent?: InputParentNode
  ): InputNodeGroup<InputConstantNode> | null {
    return findLastNode<InputConstantNode>(
      node,
      (node) => node.type == "constant",
      parent
    );
  }

  function findFirstConstantNode(
    node: InputNode,
    parent?: InputParentNode
  ): InputNodeGroup<InputConstantNode> | null {
    return findFirstNode<InputConstantNode>(
      node,
      (node) => node.type == "constant",
      parent
    );
  }

  function moveNodeUp(node: InputOperatorNode, parent: InputOperatorNode) {
    const index = parent.args.indexOf(node);
    if (index == -1) {
      throw new Error("Node not found in parent");
    }

    if (index == 0) {
      const newNode = createOperator({
        op: node.op,
        args: [
          node.args[0],
          createOperator({
            op: parent.op,
            args: [node.args[1], parent.args[1]]
          })
        ],
        id: node.id
      });
      replaceNode(parent, newNode);
      console.log("replace", parent, "with", newNode);
      return newNode;
    } else {
      const newNode = createOperator({
        op: node.op,
        args: [
          createOperator({
            op: parent.op,
            args: [parent.args[0], node.args[0]]
          }),
          node.args[1]
        ],
        id: node.id
      });
      replaceNode(parent, newNode);
      console.log("replace", parent, "with", newNode);
      return newNode;
    }
  }

  /**
   * Makes sure that the endNode is after the startNode.
   */
  function ensureCorrectSelectionOrder(
    selection: NodeSelection
  ): NodeSelection {
    const { startNode, endNode, startOffset, endOffset } = selection;

    if (startNode.id == endNode.id) {
      if (startOffset > endOffset) {
        return {
          startNode: endNode,
          endNode: startNode,
          startOffset: endOffset,
          endOffset: startOffset
        };
      }
    } else {
      const parentResult = getCommonParent(startNode, endNode);
      if (parentResult == null) return selection;
      const { indexA: startIndex, indexB: endIndex } = parentResult;

      if (startIndex > endIndex) {
        return {
          startNode: endNode,
          endNode: startNode,
          startOffset: endOffset,
          endOffset: startOffset
        };
      }
    }

    return selection;
  }

  /**
   * Returns the operator node whose operator symbol is enclosed by the selection.
   * If the selection does not enclose an operator, return null.
   * e.g. "5 [*] 6" ---> "[*]"
   * e.g. "5 [* 6]" ---> null
   */
  function getEnclosedOperator(selection: NodeSelection) {
    if (selection.endNode == selection.startNode) {
      return null;
    }

    if (selection.endOffset != 0) return null;

    const startNode = selection.startNode;
    const endNode = selection.endNode;

    const maxStartOffset = getMaxOffset(startNode);
    if (selection.startOffset != maxStartOffset) return null;

    const parentResult = getCommonParent(startNode, endNode);
    if (parentResult == null) return null;

    if (parentResult.parent.type != "operator") return null;
    return parentResult.parent;
  }

  /**
   * If we're adjacent to an operator sign of an operator node,
   * return the operator node.
   */
  function getNeighbourOperator(node: InputNode, offset: number) {
    // check if we're at the border of the node
    const maxOffset = getMaxOffset(node);
    if (offset != 0 && offset != maxOffset) return null;

    const path = getNodePath(state.root, node);
    if (path == null) return null;

    let parent = path[path.length - 1];
    while (parent) {
      if (parent.type == "function" || parent.type == "parenthesis") {
        // if we're inside a function or parenthesis,
        // we can't be adjacent to an operator sign.
        return null;
      }

      if (parent.type != "operator") {
        console.warn("parent is not an operator", parent);
        return null;
      }

      const index = parent.args.indexOf(node);

      if (index == 0 && offset == maxOffset) {
        // we're left of the operator sign
        return parent;
      }
      if (index == 1 && offset == 0) {
        // we're right of the operator sign
        return parent;
      }

      node = parent;
      parent = path[path.indexOf(parent) - 1];
      if (parent == null) return null;
    }

    return null;
  }

  /**
   * Returns the function node whose name symbol is enclosed by the selection.
   */
  function getSelectedFunction(selection: NodeSelection) {
    if (selection.endNode == selection.startNode) {
      return null;
    }

    if (selection.endOffset != 0) return null;
    if (selection.startOffset != 0) return null;

    const startNode = selection.startNode;
    const endNode = selection.endNode;

    if (startNode.type != "function") return null;

    const endNodePath = getNodePath(state.root, endNode);
    if (endNodePath == null) return null;

    let n = endNode;
    let i = endNodePath.length - 1;
    let p = endNodePath[i];
    while (n) {
      if (n == startNode) {
        return startNode;
      }
      const index = p.args.indexOf(n);
      if (index != 0) {
        return null;
      }
      n = endNodePath[i--];
      p = endNodePath[i];
    }

    return null;
  }

  /**
   * Take a selection spanning across multiple nodes,
   * remove everything inside the selection and merge the adjacent nodes.
   * Return the new selection.
   */
  function removeNodeSelection(selection: NodeSelection): {
    node: InputNode;
    startOffset: number;
    removedNode: InputNode | null;
  } {
    const orderedSelection = ensureCorrectSelectionOrder(selection);
    const { startNode, endNode, startOffset, endOffset } = orderedSelection;

    if (startNode == endNode) {
      if (startOffset == endOffset) {
        return { node: startNode, startOffset: startOffset, removedNode: null };
      }

      // Remove the selection inside a single node
      if (startNode.type == "constant") {
        const textToRemove = startNode.value.slice(startOffset, endOffset);

        const textBefore = startNode.value.slice(0, startOffset);
        const textAfter = startNode.value.slice(endOffset);

        startNode.value = textBefore + textAfter;

        return {
          node: startNode,
          startOffset: textBefore.length,
          removedNode: createConstant({ value: textToRemove })
        };
      }

      const replaced = replaceNode(startNode, createConstant({ value: "" }));
      if (replaced == null) {
        console.warn("could not remove node");
        return {
          node: startNode,
          startOffset: startOffset,
          removedNode: null
        };
      }

      return {
        node: replaced.node,
        startOffset: replaced.startOffset,
        removedNode: startNode
      };
    }

    const commonAncestorGroup = getCommonParent(startNode, endNode);

    if (commonAncestorGroup == null) {
      console.warn("No common ancestor");
      return {
        node: selection.endNode,
        startOffset: selection.endOffset,
        removedNode: null
      };
    }

    const { parent: commonAncestor } = commonAncestorGroup;

    const splitStart = splitNodeTo(commonAncestor, startNode, startOffset);

    if (splitStart == null) {
      console.warn("could not split start node");
      return {
        node: selection.endNode,
        startOffset: selection.endOffset,
        removedNode: null
      };
    }

    const splitEnd = splitNodeTo(commonAncestor, endNode, endOffset);

    if (splitEnd == null) {
      console.warn("could not split end node");
      return {
        node: selection.endNode,
        startOffset: selection.endOffset,
        removedNode: null
      };
    }

    const newRest = joinTrees(splitStart.nodeLeft, splitEnd.nodeRight);
    if (newRest == null) {
      console.warn("could not join trees");
      return {
        node: selection.endNode,
        startOffset: selection.endOffset,
        removedNode: null
      };
    }

    const replaced = replaceNode(commonAncestor, newRest.node);

    let removed = null;
    if (commonAncestor.type == "operator") {
      removed = createOperator({
        op: commonAncestor.op,
        args: [splitStart.nodeRight, splitEnd.nodeLeft]
      });
      if (removed == null) {
        console.warn("could not join removed trees");
        return {
          node: replaced.node,
          startOffset: replaced.startOffset,
          removedNode: null
        };
      }
    } else {
      const joined = joinTrees(splitStart.nodeRight, splitEnd.nodeLeft);
      if (joined == null) {
        console.warn("could not join removed trees");
        return {
          node: replaced.node,
          startOffset: replaced.startOffset,
          removedNode: null
        };
      }
      removed = createParenthesis({
        args: [joined?.node],
        closed: true,
        opened: true
      });
    }

    return {
      node: newRest.focusNode,
      startOffset: newRest.offset,
      removedNode: removed
    };
  }

  function insertCharacter(
    node: InputNode,
    offset: number,
    char: string
  ): NodeFocusPosition | undefined {
    let text = char.replaceAll(",", ".");
    
    if (node.type == "constant") {
      node.value =
        node.value.slice(0, offset) + text + node.value.slice(offset);
      return { node, startOffset: offset + text.length };
    }

    if (offset == 0) {
      const constantNode = createConstant({ value: text });
      const operator = createOperator({
        op: "*",
        args: [constantNode, node]
      });
      replaceNode(node, operator);

      return {
        node: constantNode,
        startOffset: getMaxOffset(constantNode)
      };
    } else {
      const constantNode = createConstant({ value: text });
      const operator = createOperator({
        op: "*",
        args: [node, constantNode]
      });
      replaceNode(node, operator);

      return {
        node: constantNode,
        startOffset: text.length
      };
    }
  }

  function insertOperator(
    node: InputNode,
    offset: number,
    options: { op: ExpressionOperator | "!" | "="; args?: InputNode[] },
    extra: { constantValue?: string } = {}
  ): NodeFocusPosition | undefined {
    const nodeGroup = findNode(node.id);
    if (!nodeGroup) return;

    let nextFocusNode = node;
    let nextStartOffset = offset;

    const op = options.op;

    const neighbourOperator = getNeighbourOperator(node, offset);
    if (neighbourOperator) {
      if (neighbourOperator.op == "") {
        updateNode(neighbourOperator, {
          op: getValidOperator(op)
        });
        return { node: node, startOffset: offset };
      }

      // JOIN OPERATORS

      if (offset == 0) {
        if (neighbourOperator.op == "<" && op == "=") {
          updateNode(neighbourOperator, { op: "<=" });
          return { node: node, startOffset: offset };
        }
        if (neighbourOperator.op == ">" && op == "=") {
          updateNode(neighbourOperator, { op: ">=" });
          return { node: node, startOffset: offset };
        }
      }
      if (offset == getMaxOffset(node)) {
        if (neighbourOperator.op == "==" && op == ">") {
          updateNode(neighbourOperator, { op: ">=" });
          return { node: node, startOffset: offset };
        }
        if (neighbourOperator.op == "==" && op == "<") {
          updateNode(neighbourOperator, { op: "<=" });
          return { node: node, startOffset: offset };
        }
      }
    }

    if (node.type == "constant") {
      const textBefore = node.value.slice(0, offset);
      const textAfter = node.value.slice(offset);

      // split the current node at the offset:
      let newNodeA: InputNode = createConstant({ value: textBefore });
      const newNodeB = createConstant({ value: textAfter });

      if (options.args && options.args.length > 0) {
        const joined = joinTrees(newNodeA, options.args[0])?.node;
        newNodeA = joined || newNodeA;
      }

      replaceNode(
        node,
        createOperator({
          op: op,
          args: [newNodeA, newNodeB]
        })
      );

      nextFocusNode = newNodeB;
      nextStartOffset = 0;
    } else {
      if (offset == 0) {
        const constantNode = createConstant({
          value: extra.constantValue || ""
        });
        const newOperator = createOperator({
          op: op,
          args: [constantNode, node]
        });
        replaceNode(node, newOperator);
        nextFocusNode = node;
        nextStartOffset = 0;
      } else {
        const constantNode = createConstant({
          value: extra.constantValue || ""
        });
        const newOperator = createOperator({
          op: op,
          args: [node, constantNode]
        });
        replaceNode(node, newOperator);
        nextFocusNode = constantNode;
        nextStartOffset = constantNode.value.length;
      }
    }

    console.log("next focus node", nextFocusNode.type, nextFocusNode);

    return { node: nextFocusNode || node, startOffset: nextStartOffset };
  }

  function insertFunction(
    node: InputNode,
    offset: number,
    options: SetRequired<Partial<InputFunctionNode>, "name">
  ): NodeFocusPosition {
    const code = options.name;

    const args = fillFunctionArguments(code, options.args);

    const newFunctionNode = createFunction({
      name: code,
      args
    });

    const originalNode = node;

    if (node.type == "constant") {
      if (node.value.trim() == "") {
        replaceNode(originalNode, newFunctionNode);
      } else {
        const textBefore = node.value.slice(0, offset);
        const textAfter = node.value.slice(offset);

        // combine the new node with a multiplication
        if (textBefore != "") {
          const operatorNode = createOperator({
            op: "*",
            args: [
              createConstant({
                value: textBefore
              }),
              newFunctionNode
            ]
          });
          replaceNode(node, operatorNode);
          node = operatorNode;
        } else {
          replaceNode(originalNode, newFunctionNode);
          node = newFunctionNode;
        }

        if (textAfter != "") {
          const operatorNode = createOperator({
            op: "*",
            args: [
              node,
              createConstant({
                value: textAfter
              })
            ]
          });
          replaceNode(node, operatorNode);
          node = operatorNode;
        }
      }
    } else {
      if (offset == 0) {
        const newOperator = createOperator({
          op: "*",
          args: [newFunctionNode, node]
        });
        replaceNode(node, newOperator);
      } else {
        const newOperator = createOperator({
          op: "*",
          args: [node, newFunctionNode]
        });
        replaceNode(node, newOperator);
      }
    }

    const focusArgNode =
      options.args &&
      options.args.length > 0 &&
      args.length > options.args.length
        ? newFunctionNode.args[1]
        : newFunctionNode.args[0];

    return {
      node: focusArgNode,
      startOffset: 0,
      endOffset: getMaxOffset(focusArgNode)
    };
  }

  function fillFunctionArguments(code: FunctionName, args?: InputNode[]) {
    // create default arguments
    const functionArgsNodes: InputNode[] = functions[code].defaultArgs.map(
      (v) =>
        createConstant({
          value: `${v}`
        })
    );
    for (let i = 0; i < functionArgsNodes.length; i++) {
      if (args?.[i] != null) {
        functionArgsNodes[i] = args[i];
      }
    }

    return functionArgsNodes;
  }

  function insertItem(
    node: InputNode,
    offset: number,
    options: SetRequired<Partial<InputItemNode>, "uid">
  ) {
    const newItemNode = createItem(options);

    return insertNode(node, offset, newItemNode);
  }

  function insertVariable(
    node: InputNode,
    offset: number,
    options: SetRequired<Partial<InputVariableNode>, "code">
  ) {
    const newVariableNode = createVariable(options);

    return insertNode(node, offset, newVariableNode);
  }

  function insertNode(
    node: InputNode,
    offset: number,
    nodeToInsert: InputNode
  ): NodeFocusPosition {
    const originalNode = node;

    if (node.type == "constant") {
      const textBefore = node.value.slice(0, offset);
      const textAfter = node.value.slice(offset);

      replaceNode(originalNode, nodeToInsert);
      node = nodeToInsert;

      // combine the new node with a multiplication
      if (textBefore != "") {
        const operatorNode = createOperator({
          op: "*",
          args: [
            createConstant({
              value: textBefore
            }),
            node
          ]
        });
        replaceNode(node, operatorNode);
        node = operatorNode;
      }

      if (textAfter != "") {
        const operatorNode = createOperator({
          op: "*",
          args: [
            node,
            createConstant({
              value: textAfter
            })
          ]
        });
        replaceNode(node, operatorNode);
        node = operatorNode;
      }
    } else {
      if (offset == 0) {
        const newOperator = createOperator({
          op: "*",
          args: [nodeToInsert, node]
        });
        replaceNode(node, newOperator);
      } else {
        const newOperator = createOperator({
          op: "*",
          args: [node, nodeToInsert]
        });
        replaceNode(node, newOperator);
      }
    }

    if (state.insertMode == "coarse") {
      return {
        node: nodeToInsert,
        startOffset: 0,
        endOffset: getMaxOffset(nodeToInsert)
      };
    }

    return { node: nodeToInsert, startOffset: 1 };
  }

  function insertParenthesis(
    node: InputNode,
    offset: number,
    options: { type: "opening" | "closing"; args?: InputNode[] }
  ): NodeFocusPosition {
    const type = options.type;

    // If there's args defined, insert a closed parenthesis.
    if (options.args && options.args.length > 0) {
      const newParenthesisNode = createParenthesis({
        args: [options.args[0]],
        opened: true,
        closed: true
      });
      return insertNode(node, offset, newParenthesisNode);
    }

    if (type == "opening") {
      // TODO: separate operators if the parenthesis was set in the middle.

      // include not only the constant node, but also all parents
      // that are not already in a parenthesis or function
      let parenthesisContent: InputNode = node;
      let parentGroup = findNode(node.id)!;
      if (parentGroup) {
        let p = parentGroup.parent;

        while (p && p.type != "function" && p.type != "parenthesis") {
          parentGroup = findNode(p.id)!;
          parenthesisContent = p;
          p = parentGroup?.parent;
        }
      }

      const split = splitNodeTo(parenthesisContent, node, offset, true);
      if (split == null) {
        throw new Error("Could not split node");
      }
      const { nodeLeft, nodeRight } = split;

      const parenthesisNode = createParenthesis({
        args: [nodeRight],
        opened: true,
        closed: false
      });

      const joined = joinTrees(nodeLeft, parenthesisNode);
      if (!joined) {
        throw new Error("Could not join trees");
      }

      node = findFirstConstantNode(nodeRight)?.node || node;

      replaceNode(parenthesisContent, joined?.node);

      return { node, startOffset: 0 };
    } else {
      // closing parenthesis

      const openingParenthesis = findReachableOpeningParenthesis(node, offset);

      if (openingParenthesis == null) {
        // include not only the constant node, but also all parents
        // that are not already in a parenthesis or function
        let parenthesisContent: InputNode = node;
        let parentGroup = findNode(node.id)!;
        if (parentGroup) {
          let p = parentGroup.parent;

          while (p && p.type != "function" && p.type != "parenthesis") {
            parentGroup = findNode(p.id)!;
            parenthesisContent = p;
            p = parentGroup?.parent;
          }
        }

        const split = splitNodeTo(parenthesisContent, node, offset, true);
        if (split == null) {
          throw new Error("Could not split node");
        }
        const { nodeLeft, nodeRight } = split;

        const parenthesisNode = createParenthesis({
          args: [nodeLeft],
          opened: false,
          closed: true
        });

        const joined = joinTrees(parenthesisNode, nodeRight);
        if (!joined) {
          throw new Error("Could not join trees");
        }

        replaceNode(parenthesisContent, joined.node);
        return { node: parenthesisNode, startOffset: 1, endOffset: 1 };
      }

      if (node == openingParenthesis.node) {
        if (offset == 1) {
          // if the node is the opening parenthesis itself,
          // just close it.
          openingParenthesis.node.closed = true;
          return { node, startOffset: 1 };
        } else {
          // we're just left of the opening parenthesis.
          // we have to find the previous parent.
          const parentGroup = findNode(node.id);
          if (!parentGroup || !parentGroup.parent) {
            throw new Error("can't close parenthesis");
          }
          node = parentGroup.parent;
          offset = 0;
        }
      }

      const subpath = getSubpath(node, openingParenthesis.node);

      if (subpath) {
        // subpath exists, so the node is a child of the opening parenthesis.
        const split = splitNodeTo(openingParenthesis.node, node, offset);

        if (split == null) {
          throw new Error("Could not split node");
        }

        console.log("split", split);

        openingParenthesis.node.args = [split.nodeLeft];

        console.log("close parenthesis");
        openingParenthesis.node.closed = true;

        if (isEmptyNode(split.nodeRight)) {
          // if there's nothing to place right of the closing parenthesis,
          // just return here.
          return {
            node: openingParenthesis.node,
            startOffset: 1
          };
        }

        const nextNodeOnRight = findLeft(
          split.nodeRight,
          (node) => node.type != "operator"
        );

        if (
          nextNodeOnRight &&
          isEmptyNode(nextNodeOnRight.node)
        ) {
          // if the first node on the right is a constant with no value,
          // move the parenthesis here
          replaceNode(openingParenthesis.node, split.nodeRight);
          replaceNode(nextNodeOnRight.node, openingParenthesis.node);

          return { node: openingParenthesis.node, startOffset: 1 };
        } else {
          // if there's something to place right of the closing parenthesis,
          // add an operator node between the closing parenthesis and the node
          const operatorNode = createOperator({
            op: "*",
            args: [openingParenthesis.node, split.nodeRight]
          });
          replaceNode(openingParenthesis.node, operatorNode);
        }

        return getFocussable(split.nodeRight, 0);
      }

      console.log("found opening", openingParenthesis);
    }

    return { node, startOffset: offset };
  }

  function splitNodeTo(
    sourceNode: InputNode,
    nodeToFind: InputNode,
    offset: number,
    splitSourceNode = false
  ): { nodeLeft: InputNode; nodeRight: InputNode } | undefined {
    const subpath = getSubpath(nodeToFind, sourceNode);

    if (subpath == null) {
      console.warn("No subpath found");
      return;
    }

    let nodeLeft: InputNode;
    let nodeRight: InputNode;

    if (nodeToFind.type == "constant") {
      nodeLeft = createConstant({
        value: nodeToFind.value.slice(0, offset)
      });
      nodeRight = createConstant({
        value: nodeToFind.value.slice(offset)
      });
    } else {
      if (offset == 0) {
        nodeLeft = createConstant({
          value: ""
        });
        nodeRight = nodeToFind;
      } else {
        nodeLeft = nodeToFind;
        nodeRight = createConstant({
          value: ""
        });
      }
    }

    // console.log("nodeLeft", cloneDeep(nodeLeft), "nodeRight", cloneDeep(nodeRight));

    let i = 0;
    let n: InputNode = nodeToFind;
    let parent = subpath[subpath.length - 1];
    let index = 0;

    while (parent) {
      if (parent == sourceNode && !splitSourceNode) break; //  && sourceNode.type == "parenthesis"

      index = parent.args.indexOf(n);

      if (index == 0) {
        if (parent.type == "parenthesis") {
          nodeLeft = createParenthesis({
            args: [nodeLeft],
            opened: true,
            closed: false
          });
        }
        parent.args = [nodeRight, ...parent.args.slice(1)];
        nodeRight = parent;
      } else if (index > 0) {
        parent.args = [
          ...parent.args.slice(0, index),
          nodeLeft,
          ...parent.args.slice(index + 1)
        ];
        nodeLeft = parent;
      }

      // if (parent == sourceNode) break;

      i++;
      n = parent;
      parent = subpath[subpath.length - 1 - i];
    }

    // console.log(
    //   "split",
    //   nodeToFind,
    //   "in",
    //   sourceNode,
    //   "into",
    //   cloneDeep(nodeLeft),
    //   "and",
    //   cloneDeep(nodeRight)
    // );

    return { nodeLeft, nodeRight };

    // EXAMPLE A
    // [5 + [[[ 56 * 45] * 5] * 5]]
    // [5 + [[[ 56 * 4 | 5] * 5] * 5]]                    SPLIT BETWEEN 4 AND 5
    // 1. loop: [5 + [[[ 56 * 4 | 5] * 5] * 5]            nodeLeft: 4; nodeRight: 5
    // 2. loop: [5 + [[[ 56 * 4 ]    * 5] * 5]            nodeLeft: 56 * 4; nodeRight: 5
    // 3. loop: [5 + [           [ 5 * 5] * 5]            nodeLeft: 56 * 4; nodeRight: 5 * 5
    // 4. loop: [5 + [           [ 5 * 5] * 5]            nodeLeft: 56 * 4; nodeRight: [5*5] * 5
    // 5. loop: [5 + [56*4] ]                             nodeLeft: 5 + [56 * 4]; nodeRight: [5*5] * 5

    // EXAMPLE B
    // [[[7 * 7] * 9] + [[6 * 44] + 6]]
    // [[[7 * 7] * 9] + [[6 * 4 | 4] + 6]]                SPLIT BETWEEN 4 AND 4
    // 1. loop: [[[7 * 7] * 9] + [[6 * 4 | 4] + 6]]       nodeLeft: 4; nodeRight: 4
    // 2. loop: [[[7 * 7] * 9] + [[6 * 4] + 6]]           nodeLeft: 6 * 4; nodeRight: 4
    // 3. loop: [[[7 * 7] * 9] + [4 + 6]]                 nodeLeft: 6 * 4; nodeRight: 4 + 6
    // 4. loop: [[[7 * 7] * 9] + [4 + 6]]                 nodeLeft: [[7 * 7] * 9] + [4+6]; nodeRight: 4 + 6

    // EXAMPLE C
    // [[6 * 5] + 5]
    // [[6 * |5] + 5]                                     SPLIT BEFORE THE FIRST 5
    // 1. loop: [[6 * |5] + 5]                            nodeLeft: ""; nodeRight: 5
    // 2. loop: [[6 * 5] + 5]                             nodeLeft: 6; nodeRight: 5
  }

  /**
   * Merges the given node trees in the middle.
   */
  function joinTrees(treeLeft: InputNode, treeRight: InputNode) {
    const rightNode = findLeft(
      treeRight,
      (node) => node.type != "operator"
    );
    if (rightNode == null) {
      console.warn("No leftmost node");
      return;
    }

    const leftNode = findRight(
      treeLeft,
      (node) => node.type != "operator"
    );
    if (leftNode == null) {
      console.warn("No rightmost node");
      return;
    }

    let focusNode = treeRight;
    let offset = 0;

    // if the left or right tree is empty, return the other tree
    if (treeRight.type == "constant" && treeRight.value == "") {
      return {
        node: treeLeft,
        focusNode: leftNode.node,
        offset: getMaxOffset(leftNode.node)
      };
    }
    if (treeLeft.type == "constant" && treeLeft.value == "") {
      return {
        node: treeRight,
        focusNode: rightNode.node,
        offset: 0
      };
    }

    // else if (leftNode.node.type == "parenthesis" && !leftNode.node.closed) {
    //   // if the left node is an opening parenthesis, merge the right tree into it
    //   if (rightNode.node == treeRight) {
    //     treeRight = leftNode.node;
    //   } else {
    //     const replaced = replaceNode(rightNode.node, leftNode.node, treeRight);
    //     focusNode = replaced.node;
    //     offset = replaced.offset;
    //   }
    // }

    // console.log("join:");
    // console.log("leftNode", cloneDeep(leftNode));
    // console.log("rightNode", cloneDeep(rightNode));

    if (rightNode.node.type == "constant" && leftNode.node.type == "constant") {
      offset = leftNode.node.value.length;
      focusNode = leftNode.node;

      leftNode.node.value += rightNode.node.value;

      if (rightNode.node == treeRight) {
        treeRight = treeLeft;
      } else {
        const replaced = replaceNode(rightNode.node, treeLeft, treeRight);
        focusNode = replaced.node;
        offset = replaced.startOffset;
      }
    } else if ((rightNode.node.type == "parenthesis" || rightNode.node.type == "function") && leftNode.node.type == "constant" && leftNode.node.value == "") {
      if (leftNode.node == treeLeft) {
        treeLeft = rightNode.node;
      } else {
        const replaced = replaceNode(leftNode.node, treeRight, treeLeft);
        focusNode = replaced.node;
        offset = replaced.startOffset;
      }

      return {
        node: treeLeft,
        focusNode,
        offset
      };
    } else {
      const newOperator = createOperator({
        op: "*",
        args: [treeLeft, rightNode.node]
      });

      if (rightNode.node == treeRight) {
        treeRight = newOperator;
      } else {
        const replaced = replaceNode(rightNode.node, newOperator, treeRight);
        focusNode = replaced.node;
        offset = replaced.startOffset;
      }
    }

    return {
      node: treeRight,
      focusNode,
      offset
    };
  }

  function findReachableOpeningParenthesis(
    node: InputNode,
    offset: number
  ): InputNodeGroup<InputParenthesisNode> | null {
    // A parenthesis node is reachable as long as we don't move out of functions.
    // Try finding the closest parenthesis first.

    if (node.type == "parenthesis" && !node.closed && offset == 1) {
      return findNode(node.id) as InputNodeGroup<InputParenthesisNode>;
    }

    const parent = findNode(node.id)?.parent;
    if (parent == null) return null;

    let n: InputNode | undefined = node;
    let p: InputParentNode | undefined = parent;
    let i = parent.args.indexOf(node);

    while (p && p.type != "function") {
      const previousNode = p.args[i];
      const parenthesis = findLastNode<InputParenthesisNode>(
        previousNode,
        (node) =>
          node.type == "parenthesis" && node.opened && node.closed == false
      );

      if (parenthesis) return parenthesis;

      i--;
      if (i < 0) {
        if (p.type == "parenthesis" && p.opened) {
          return findNode(p.id) as InputNodeGroup<InputParenthesisNode>;
        }

        n = p;
        p = findNode(p.id)?.parent;

        if (p == null) return null;
        i = p.args.indexOf(n);
      }
    }

    return null;
  }

  function getSubpath(
    node: InputNode,
    parent: InputNode
  ): InputParentNode[] | null {
    if (node == parent) {
      return [];
    }
    if ("args" in parent) {
      for (const arg of parent.args) {
        const subpath = getSubpath(node, arg);
        if (subpath != null) {
          subpath.unshift(parent);
          return subpath;
        }
      }
    }
    return null;
  }

  function isParenthesisNode(node: InputNode): node is InputParenthesisNode {
    return node.type == "parenthesis";
  }

  function isNodeWithAnchors(node: InputNode): node is InputNodeWithAnchors {
    return (
      node.type == "function" ||
      node.type == "item" ||
      node.type == "variable" ||
      node.type == "parenthesis"
    );
  }

  // function isParentNode(node: InputNode): node is InputParentNode {
  //   return "args" in node;
  // }

  interface NestedStringTree {
    [key: string]: string | NestedStringTree;
  }

  function getAsTree(node: InputNode) {
    if (node.type == "constant") {
      return node.id;
    }

    const tree: NestedStringTree | string = {};

    if ("args" in node) {
      let i = 0;
      const total = node.args.length;

      for (const arg of node.args) {
        i++;
        const prefix = Array(i).fill("_").join("").padStart(total, "-");
        tree[`${prefix}${getNodeLabel(arg)}`] = getAsTree(arg);
      }
    }

    return tree;
  }

  function log() {
    console.log("tree", {
      [`${getNodeLabel(state.root)}`]: getAsTree(state.root)
    });
  }

  function getNodeLabel(node: InputNode) {
    if (node.type == "constant") {
      return node.value;
    }
    if (node.type == "operator") {
      return node.op;
    }
    if (node.type == "function") {
      return node.name;
    }
    if (node.type == "item") {
      return node.title;
    }
    if (node.type == "variable") {
      return node.code;
    }
    if (node.type == "parenthesis") {
      return `${node.opened ? "(" : ""}${node.closed ? ")" : ""}`;
    }
    return "";
  }

  function replaceSelection(
    selection: NodeSelection,
    insertFunction?: (
      node: InputNode,
      offset: number,
      removedNode: InputNode | null
    ) =>
      | { node: InputNode; startOffset: number; endOffset?: number }
      | undefined
  ) {
    const { node, startOffset, removedNode } = removeNodeSelection({
      startNode: selection.startNode,
      startOffset: selection.startOffset,
      endNode: selection.endNode,
      endOffset: selection.endOffset
    });

    let nextFocusNode = node;
    let nextStartOffset = startOffset;
    let nextEndOffset = startOffset;

    if (insertFunction) {
      const inserted = insertFunction(node, startOffset, removedNode);

      if (inserted) {
        nextFocusNode = inserted.node ?? nextFocusNode;
        nextStartOffset = inserted.startOffset;
        nextEndOffset = inserted.endOffset ?? nextStartOffset;
      }
    }

    if (state.insertMode == "coarse") {
      nextStartOffset = 0;
      nextEndOffset = getMaxOffset(nextFocusNode);
    }

    updateSelection({
      startNode: nextFocusNode,
      startOffset: nextStartOffset,
      endNode: nextFocusNode,
      endOffset: nextEndOffset
    });

    emitUpdate();

    return {
      node: nextFocusNode,
      offset: nextStartOffset
    };
  }

  /**
   * Evaluates the given node and returns the result as an ExpressionValue.
   */
  function evaluateNode(node: InputNode): ExpressionValue {
    const ctx = { ...toValue(context) };
    const nodePath = getIndexedNodePath(state.root, node);
    if (nodePath == null) {
      console.warn("Could not find node path");
      return {
        value: 0,
        minValue: null,
        maxValue: null,
        unit: "",
        unitValid: false,
        valid: false
      };
    }
    const key = ctx.randomKey + nodePath[nodePath.length - 1].path;
    ctx.randomKey = key;

    // Special case handling: if the node is the root node and starts with a "==" operator, we just evaluate the right side of the operator.
    if (node.type == "operator" && node.op == "==" && nodePath.length == 1) {
      const leftNode = node.args[0];
      if (leftNode.type == "constant" && leftNode.value.trim() == "") {
        const rightNode = node.args[1];
        return evaluateNode(rightNode);
      }
    }

    const mathNode = inputNodeToMathNode(node, ctx);
    return evaluateMathNode(mathNode, ctx);
  }

  /**
   * Make sure all nodes are in the correct order.
   */
  function fixPrecedence(rootNode?: InputNode): boolean {
    if (!rootNode) rootNode = state.root;

    const leftMost = findLeft(rootNode, (n) => !("args" in n));
    if (!leftMost) return false;

    let n = leftMost.node;
    let p: InputParentNode | undefined = leftMost.parent;
    let i: number | undefined = leftMost.index;
    while (n != rootNode) {
      if (!p || i == null) {
        console.warn("No more parent or index", n, p, i);
        return false;
      }

      if (n.type == "operator" && p?.type == "operator") {
        const fixed = fixPrecedenceUpwards(n, p, i);
        if (!fixed) return false;
        n = fixed.node;
        p = fixed.parent;
        i = fixed.index;
      }

      if (!p || i == null) {
        console.warn("No more parent or index");
        return false;
      }

      const maxIndex = p.args.length - 1;
      if (i >= maxIndex) {
        // Move up
        // console.log("↑ ↑ ↑ ↑");
        const upper = findNodeIn(rootNode, p.id);
        if (!upper) {
          console.warn("no parent found");
          return false;
        }
        n = upper.node;
        p = upper.parent;
        i = upper.index;
      } else {
        // Move right
        // console.log("→ → → →");
        const nextArg = p.args[i + 1];
        if (!("args" in nextArg)) {
          n = nextArg;
          i = i + 1;
        } else {
          const leftMostChild = findLeft(nextArg, (n) => !("args" in n));
          if (!leftMostChild) {
            console.warn("no leftmost node found");
            return false;
          }
          n = leftMostChild.node;
          p = leftMostChild.parent;
          i = leftMostChild.index;
        }
      }
    }

    return true;
  }

  /**
   * Replaces all parentheses with their content.
   */
  function disolveAllParentheses(rootNode?: InputNode): boolean {
    if (!rootNode) rootNode = state.root;

    const leftMost = findLeft(rootNode, (n) => !("args" in n));
    if (!leftMost) return false;

    if (rootNode.type == "parenthesis") {
      const content = rootNode.args[0];
      replaceNode(rootNode, content);
      rootNode = content;
    }

    let n = leftMost.node;
    let p: InputParentNode | undefined = leftMost.parent;
    let i: number | undefined = leftMost.index;
    while (n != rootNode) {
      if (!p || i == null) {
        console.warn("No more parent or index", n, p, i);
        return false;
      }

      if (n.type == "parenthesis") {
        const content = n.args[0];

        // preserve selection
        if (selection.startNode == n) {
          if (selection.startOffset == 0) {
            const focusPos = getFocussable(content, 0);
            updateSelection({
              startNode: focusPos.node,
              startOffset: focusPos.startOffset
            });
          } else if (selection.startOffset == 1) {
            const focusPos = getFocussable(
              content,
              getMaxOffset(content),
              "left"
            );
            updateSelection({
              startNode: focusPos.node,
              startOffset: focusPos.startOffset
            });
          }
        }

        if (selection.endNode == n) {
          if (selection.endOffset == 0) {
            const focusPos = getFocussable(content, 0);
            updateSelection({
              endNode: focusPos.node,
              endOffset: focusPos.startOffset
            });
          } else if (selection.endOffset == 1) {
            const focusPos = getFocussable(
              content,
              getMaxOffset(content),
              "left"
            );
            updateSelection({
              endNode: focusPos.node,
              endOffset: focusPos.startOffset
            });
          }
        }

        replaceNode(n, content);
        n = content;
      }

      if (!p || i == null) {
        console.warn("No more parent or index");
        return false;
      }

      const maxIndex = p.args.length - 1;
      if (i >= maxIndex) {
        // Move up
        // console.log("↑ ↑ ↑ ↑");
        const upper: InputNodeGroup<InputNode> | null = findNodeIn(
          rootNode,
          p.id
        );
        if (!upper) {
          console.warn("no parent found");
          return false;
        }
        n = upper.node;
        p = upper.parent;
        i = upper.index;
      } else {
        // Move right
        // console.log("→ → → →");
        const nextArg = p.args[i + 1];
        if (!("args" in nextArg)) {
          n = nextArg;
          i = i + 1;
        } else {
          const leftMostChild = findLeft(nextArg, (n) => !("args" in n));
          if (!leftMostChild) {
            console.warn("no leftmost node found");
            return false;
          }
          n = leftMostChild.node;
          p = leftMostChild.parent;
          i = leftMostChild.index;
        }
      }
    }

    return true;
  }

  /**
   * Make sure all nodes that are not in the correct order
   * are wrapped in parentheses.
   */
  function wrapPrecendentsInParentheses(rootNode?: InputNode): boolean {
    if (!rootNode) rootNode = state.root;

    const leftMost = findLeft(rootNode, (n) => !("args" in n));
    if (!leftMost) return false;

    let n = leftMost.node;
    let p: InputParentNode | undefined = leftMost.parent;
    let i: number | undefined = leftMost.index;
    while (n != rootNode) {
      if (!p || i == null) {
        console.warn("No more parent or index", n, p, i);
        return false;
      }

      if (n.type == "operator" && p?.type == "operator") {
        const parentHasHigherPrecedence = hasHigherPrecedence(p.op, n.op);

        if (parentHasHigherPrecedence) {
          // wrap n in parenthesis
          const newParenthesis = createParenthesis({
            args: [n],
            opened: true,
            closed: true
          });
          p.args[i] = newParenthesis;

          const orderedSelection = ensureCorrectSelectionOrder(selection);

          // preserve selection
          if (
            orderedSelection.startNode ==
            findLeft(n, (n) => n.type != "operator")?.node &&
            orderedSelection.startOffset == 0 &&
            orderedSelection.endNode ==
            findRight(n, (n) => n.type != "operator")?.node &&
            orderedSelection.endOffset == getMaxOffset(orderedSelection.endNode) 
          ) {
            updateSelection({
              startNode: newParenthesis,
              startOffset: 0,
              endNode: newParenthesis,
              endOffset: getMaxOffset(newParenthesis)
            });
          }

          n = newParenthesis;
        }
      }

      if (!p || i == null) {
        console.warn("No more parent or index");
        return false;
      }

      const maxIndex = p.args.length - 1;
      if (i >= maxIndex) {
        // Move up
        // console.log("↑ ↑ ↑ ↑");
        const upper: InputNodeGroup<InputNode> | null = findNodeIn<InputNode>(
          rootNode,
          p.id
        );
        if (!upper) {
          console.warn("no parent found");
          return false;
        }
        n = upper.node;
        p = upper.parent;
        i = upper.index;
      } else {
        // Move right
        // console.log("→ → → →");
        const nextArg = p.args[i + 1];
        if (!("args" in nextArg)) {
          n = nextArg;
          i = i + 1;
        } else {
          const leftMostChild = findLeft(nextArg, (n) => !("args" in n));
          if (!leftMostChild) {
            console.warn("no leftmost node found");
            return false;
          }
          n = leftMostChild.node;
          p = leftMostChild.parent;
          i = leftMostChild.index;
        }
      }
    }

    return true;
  }

  /**
   *
   * @param node
   * @param parent
   */
  function fixPrecedenceUpwards(
    node: InputOperatorNode,
    parent: InputOperatorNode,
    index: number
  ) {
    const parentHasHigherPrecedence = hasHigherPrecedence(parent.op, node.op);

    if (parentHasHigherPrecedence) {
      console.log("parent has higher precedence", parent.op, node.op);
      if (index == 0) {
        const newArg1 = createOperator({
          op: parent.op,
          args: [node.args[1], parent.args[1]]
        });
        const newNode = createOperator({
          op: node.op,
          args: [node.args[0], newArg1]
        });
        replaceNode(parent, newNode);
        return findNode(node.id);
      } else {
        const newArg0 = createOperator({
          op: parent.op,
          args: [parent.args[0], node.args[0]]
        });
        const newNode = createOperator({
          op: node.op,
          args: [newArg0, node.args[1]]
        });
        replaceNode(parent, newNode);
        return findNode(node.id);
      }
    } else if (isMultiplication(parent.op) && isMultiplication(node.op)) {
      if (index == 1) {
        // a nested muliplication on the right side means that
        // the precendence is wrong. Operator should only be 
        // wrapped on the left side to ensure left to right evaluation.

        const newArg0 = createOperator({
          op: parent.op,
          args: [parent.args[0], node.args[0]]
        });
        const newNode = createOperator({
          op: node.op,
          args: [newArg0, node.args[1]]
        });
        replaceNode(parent, newNode);
        return findNode(node.id);
      }
    } else {
      console.log("node has higher precedence", parent.op, node.op);
    }

    return { node, parent, index };
  }

  function hasHigherPrecedence(
    op: ExpressionOperator | "",
    than: ExpressionOperator | ""
  ) {
    // precedence order: (* /) (+ -) (< > <= >= == !=)
    if (op == "" || than == "") return false;
    if (isComparison(op)) return false; // comparison has lowest precedence
    if (isComparison(than)) return true; // everything has higher precedence than comparison
    return isMultiplication(op) && isAddition(than);
  }

  function isComparison(op: ExpressionOperator) {
    return (
      op == "<" ||
      op == ">" ||
      op == "<=" ||
      op == ">=" ||
      op == "==" ||
      op == "!="
    );
  }

  function isMultiplication(op: ExpressionOperator | "") {
    return op == "*" || op == "/";
  }

  function isAddition(op: ExpressionOperator | "") {
    return op == "+" || op == "-";
  }

  function isEmptyNode(node: InputNode) {
    return node.type == "constant" && node.value.trim() == "";
  }

  /**
   * Traverse the node tree and call the callback for each node.
   */
  function traverse(callback: (node: InputNode, parent: InputParentNode | null) => void, node?: InputNode, parent?: InputParentNode | null) {
    if (!node) node = state.root;
    callback(node, parent ?? null);
    if (node.type === "operator" || node.type === "function") {
      for (const arg of node.args) {
        traverse(callback, arg, node);
      }
    }
  }

  return {
    state,

    selection,
    updateSelection,
    selectAll,
    setInsertMode,
  
    setExpression,

    on: eventEmitter.on,
    off: eventEmitter.off,

    // creation
    createConstant,
    createOperator,
    createFunction,
    createItem,
    createVariable,
    createParenthesis,

    getAsTree,
    log,
    toExpressionString,
    expressionToNodeTree,
    inputNodeToMathNode,

    evaluateNode,

    // getters
    getNodePath,
    getNodeLabel,
    getMaxOffset,

    findNodeIn,
    findNode,
    findPreviousNode,
    findPreviousInnerNode,
    findPreviousConstantNode,
    findNextNode,
    findNextConstantNode,
    findFirstConstantNode,
    findLastConstantNode,
    findRight,
    findLeft,
    findPreviousCursorSpot,
    findNextCursorSpot,
    getFocussable,

    getRightmostNonOperatorNode: getRightmostUnbreakableNode,

    // tree manipulation
    replaceNode,
    removeNode,
    updateNode,
    mergeWithPreviousNode,
    moveNodeUp,
    removeNodeSelection,
    orderSelection: ensureCorrectSelectionOrder,

    traverse,

    undo: () => {
      history.undo();
    },
    redo: () => {
      history.redo();
    },

    deleteBackwards() {
      if (
        selection.startNode != selection.endNode ||
        selection.startOffset != selection.endOffset
      ) {
        // if there is a selection, remove it
        console.log(
          "remove selection",
          selection.startNode,
          selection.endNode,
          selection.startOffset,
          selection.endOffset
        );
        replaceSelection(selection, undefined);
      } else {
        // if there is no selection, remove the previous node
        console.log("remove previous node");
        replaceSelection(selection, (node, offset) =>
          mergeWithPreviousNode(node, offset)
        );
      }
    },

    insertCharacter(character: string) {
      return replaceSelection(selection, (node, offset) =>
        insertCharacter(node, offset, character)
      );
    },

    insertOperator(
      operator: ExpressionOperator | "!" | "="
    ) {
      const orderedSelection = ensureCorrectSelectionOrder(selection);

      // If ONLY an operator is selected, update the operator.
      const operatorNode = getEnclosedOperator(orderedSelection);
      if (operatorNode) {
        updateNode(operatorNode, { op: getValidOperator(operator) });
        return { node: operatorNode, offset: 1 };
      }

      if (state.insertMode == "coarse") {
        // If ONLY a function is selected, replace the function with the operator.
        const selectedFunction = getSelectedFunction(orderedSelection);
        if (selectedFunction) {
          const newOperator = createOperator({
            op: operator,
            args: [
              selectedFunction.args[0] || createConstant({ value: "" }),
              selectedFunction.args[1] || createConstant({ value: "" })
            ]
          });
          replaceNode(selectedFunction, newOperator);

          const start = getFocussable(
            newOperator.args[0],
            getMaxOffset(newOperator.args[0]),
            "left"
          );
          const end = getFocussable(newOperator.args[1], 0, "right");

          updateSelection({
            startNode: start.node,
            startOffset: start.startOffset,
            endNode: end.node,
            endOffset: end.startOffset
          });

          emitUpdate();

          return { node: newOperator, offset: 1 };
        }

        // if a node is selected, use it as the first argument of the new operator
        if (
          selection.startNode == selection.endNode &&
          selection.startOffset == 0 &&
          selection.endOffset == 1
        ) {
          const newOperator = createOperator({
            op: operator,
            args: [
              selection.startNode,
              createConstant({ value: "" })
            ]
          });
          replaceNode(selection.startNode, newOperator);
          const start = getFocussable(
            newOperator.args[1],
            0,
            "left"
          );
          const end = getFocussable(newOperator.args[1], getMaxOffset(newOperator.args[1]), "right");
          updateSelection({
            startNode: start.node,
            startOffset: start.startOffset,
            endNode: end.node,
            endOffset: end.startOffset
          });
          emitUpdate();
          return { node: newOperator, offset: 1 };
        }
      }

      return replaceSelection(orderedSelection, (node, offset) => {
        const inserted = insertOperator(node, offset, { op: operator });
        if (!inserted) return;

        return {
          node: inserted.node,
          startOffset: inserted.startOffset,
          endOffset: inserted.endOffset || inserted.startOffset
        };
      });
    },

    insertItem(uid: string) {
      return replaceSelection(selection, (node, offset) => {
        const item = {
          uid: `${uid}`
        };

        return insertItem(node, offset, item);
      });
    },

    insertNode(node: InputNode) {
      return replaceSelection(selection, (currentNode, offset) => {
        return insertNode(currentNode, offset, node);
      });
    },

    insertVariable(code: string) {
      return replaceSelection(selection, (node, offset) =>
        insertVariable(node, offset, { code })
      );
    },

    insertFunction(name: FunctionName) {
      const orderedSelection = ensureCorrectSelectionOrder(selection);

      // If ONLY a function is selected, update the function.
      const selectedFunction = getSelectedFunction(orderedSelection);
      if (selectedFunction) {
        const newFunction = replaceFunction(selectedFunction, name);
        const firstArgStart = getFocussable(newFunction.args[0], 0, "right");

        updateSelection({
          startNode: newFunction,
          startOffset: 0,
          endNode: firstArgStart.node,
          endOffset: firstArgStart.startOffset
        });

        emitUpdate();

        return { node: selectedFunction, offset: 1 };
      }

      // if only an operator is selected, replace it with the function
      const selectedOperator = getEnclosedOperator(orderedSelection);
      if (selectedOperator) {
        const newFunction = createFunction({
          name,
          args: fillFunctionArguments(name, selectedOperator.args)
        });
        replaceNode(selectedOperator, newFunction);
        const firstArgStart = getFocussable(newFunction.args[0], 0, "right");

        updateSelection({
          startNode: newFunction,
          startOffset: 0,
          endNode: firstArgStart.node,
          endOffset: firstArgStart.startOffset
        });

        emitUpdate();

        return { node: selectedOperator, offset: 1 };
      }

      return replaceSelection(selection, (node, offset, removedNode) => {
        const args: InputNode[] = [];
        if (removedNode) args.push(removedNode);
        return insertFunction(node, offset, { name, args });
      });
    },

    insertParenthesis(type: "opening" | "closing") {
      return replaceSelection(selection, (node, offset, removedNode) => {
        const args: InputNode[] = [];
        if (removedNode) args.push(removedNode);
        return insertParenthesis(node, offset, { type, args });
      });
    },

    removeSelection() {
      return replaceSelection(selection);
    }
  };
}

type NodeFocusPosition<
  N extends InputNode | undefined = InputNode,
  O extends number | undefined = number
> = {
  node: N;
  startOffset: O;
  endOffset?: number;
};

export type ExpressionTree = ReturnType<typeof useExpressionTree>;
